// Quá trình làm việc của nhân sự: hợp đồng, bổ nhiệm, điều chuyển, khen thưởng, đào tạo.
// Chưa nối API nhân sự: dùng dữ liệu mẫu, các hàm tính bên dưới giữ nguyên khi có dữ liệu thật.
import dayjs from 'dayjs';
import { COMPANY_NAME } from '../../components/SidebarNew/userHub';

export type RecordType = 'contract' | 'position' | 'transfer' | 'reward' | 'training';

export interface WorkRecord {
  id: string;
  type: RecordType;
  /** Ngày hiệu lực (ISO) */
  date: string;
  /** Ngày kết thúc (hợp đồng có thời hạn) */
  endDate?: string;
  title: string;
  code?: string;
  unit: string;
  note?: string;
  details?: { label: string; value: string }[];
}

export const TYPE_META: Record<RecordType, { label: string; color: string; bg: string }> = {
  contract: { label: 'Hợp đồng', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/50 border-emerald-200 dark:border-emerald-800' },
  position: { label: 'Chức vụ', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-800' },
  transfer: { label: 'Điều chuyển', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/50 border-purple-200 dark:border-purple-800' },
  reward: { label: 'Khen thưởng', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800' },
  training: { label: 'Đào tạo', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/50 border-rose-200 dark:border-rose-800' },
};

export const WORK_RECORDS: WorkRecord[] = [
  {
    id: 'r1', type: 'contract', date: '2024-03-01', endDate: '2024-04-30', title: 'Hợp đồng thử việc', code: 'HĐTV-2024-0312',
    unit: `${COMPANY_NAME} › Phòng Dịch vụ Chăm sóc`, details: [{ label: 'Thời hạn', value: '2 tháng' }, { label: 'Mức lương thử việc', value: '85% lương chính thức' }],
  },
  {
    id: 'r2', type: 'contract', date: '2024-05-01', endDate: '2026-04-30', title: 'Hợp đồng lao động xác định thời hạn 24 tháng', code: 'HĐLĐ-2024-0501',
    unit: `${COMPANY_NAME} › Phòng Dịch vụ Chăm sóc`, details: [{ label: 'Thời hạn', value: '24 tháng' }, { label: 'Hình thức', value: 'Toàn thời gian' }],
  },
  { id: 'r3', type: 'position', date: '2024-05-01', title: 'Kỹ thuật viên', unit: `${COMPANY_NAME} › Phòng Dịch vụ Chăm sóc` },
  {
    id: 'r4', type: 'training', date: '2025-06-15', title: 'Chứng chỉ Chăm sóc da chuyên sâu', unit: 'Học viện Thẩm mỹ Khơ Thị',
    details: [{ label: 'Thời lượng', value: '40 giờ' }, { label: 'Kết quả', value: 'Đạt – Giỏi' }],
  },
  { id: 'r5', type: 'reward', date: '2026-01-10', title: 'Kỹ thuật viên xuất sắc năm 2025', unit: COMPANY_NAME, note: 'Điểm hài lòng của khách cao nhất toàn hệ thống (4,9/5).' },
  { id: 'r6', type: 'transfer', date: '2026-02-01', title: 'Điều chuyển sang chi nhánh Thảo Điền', unit: `${COMPANY_NAME} › Chi nhánh Thảo Điền`, note: 'Hỗ trợ mở rộng chi nhánh mới.' },
  { id: 'r7', type: 'position', date: '2026-02-01', title: 'Kỹ thuật viên Trưởng', unit: `${COMPANY_NAME} › Chi nhánh Thảo Điền`, note: 'Quản lý ca và hướng dẫn KTV mới.' },
  {
    id: 'r8', type: 'contract', date: '2026-05-01', endDate: '2026-11-30', title: 'Phụ lục gia hạn hợp đồng lao động', code: 'PL-2026-0501',
    unit: `${COMPANY_NAME} › Chi nhánh Thảo Điền`, details: [{ label: 'Gia hạn', value: 'đến 30/11/2026' }],
  },
];

/** Thâm niên từ ngày vào làm: "2 năm 7 tháng", "5 tháng", "12 ngày" */
export function tenure(fromISO: string, now = dayjs()): string {
  const from = dayjs(fromISO);
  if (!from.isValid() || from.isAfter(now)) return '—';
  const months = now.diff(from, 'month');
  if (months < 1) return `${now.diff(from, 'day')} ngày`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y && `${y} năm`, m && `${m} tháng`].filter(Boolean).join(' ');
}

/** Hồ sơ hiện tại suy ra từ lịch sử: chức vụ, đơn vị mới nhất, ngày vào làm, hợp đồng đang hiệu lực */
export function currentProfile(records: WorkRecord[], now = dayjs()) {
  const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));
  const past = sorted.filter(r => !dayjs(r.date).isAfter(now));
  const position = [...past].reverse().find(r => r.type === 'position');
  const latest = past[past.length - 1];
  const contract = [...past].reverse().find(r => r.type === 'contract' && (!r.endDate || !dayjs(r.endDate).isBefore(now, 'day')));
  const contractDaysLeft = contract?.endDate ? dayjs(contract.endDate).startOf('day').diff(now.startOf('day'), 'day') : null;
  return {
    joinDate: sorted[0]?.date ?? null,
    position: position?.title ?? null,
    unit: latest?.unit ?? null,
    contract: contract ?? null,
    contractDaysLeft,
  };
}

/** Trạng thái của từng mốc để hiện nhãn */
export function recordStatus(r: WorkRecord, all: WorkRecord[], now = dayjs()): { label: string; tone: 'active' | 'ended' | 'done' | 'upcoming' } {
  if (dayjs(r.date).isAfter(now, 'day')) return { label: 'Sắp hiệu lực', tone: 'upcoming' };
  if (r.type === 'contract') return r.endDate && dayjs(r.endDate).isBefore(now, 'day') ? { label: 'Đã hết hạn', tone: 'ended' } : { label: 'Đang hiệu lực', tone: 'active' };
  if (r.type === 'position') {
    const later = all.find(x => x.type === 'position' && x.date > r.date && !dayjs(x.date).isAfter(now, 'day'));
    return later ? { label: 'Đã thay đổi', tone: 'ended' } : { label: 'Đang đảm nhiệm', tone: 'active' };
  }
  return { label: 'Hoàn thành', tone: 'done' };
}
